import { ExternalLinkIcon } from 'lucide-react';
import { useGetLandingPages } from '@tinct/core-api';
import { Card } from '@tinct/web-components';
import { cn } from '@tinct/core-utils';

export type PublishedPagesCardProps = {
  className?: string;
  organizationId: string;
};

/**
 * The workspace's published pages with their custom-domain URLs. Only
 * rendered on the active state — `organizationPublishedUrl` is null
 * otherwise.
 */
export function PublishedPagesCard({
  className,
  organizationId,
}: PublishedPagesCardProps) {
  const { data } = useGetLandingPages(
    {
      staticParams: { organizationId },
      fields: {
        id: null,
        name: null,
        slug: null,
        organizationPublishedUrl: null,
      },
      offset: 0,
      limit: 100,
    },
    { enabled: !!organizationId },
  );

  const publishedPages = (data?.data ?? []).filter(
    (page) => page.organizationPublishedUrl,
  );

  if (publishedPages.length === 0) return null;

  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex flex-col gap-1.5 px-6 pt-6">
        <h3 className="font-heading text-xl font-semibold leading-7 tracking-[-0.3px] text-foreground">
          Published pages on your domain
        </h3>
      </div>
      <div className="px-6 py-6">
        <ul className="flex flex-col divide-y">
          {publishedPages.map((page) => (
            <li
              key={page.id}
              className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
            >
              <span className="truncate text-sm font-medium text-foreground">
                {page.name ?? page.slug}
              </span>
              <a
                href={page.organizationPublishedUrl ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center gap-1 font-mono text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
              >
                {page.organizationPublishedUrl}
                <ExternalLinkIcon className="size-3" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
